/**
 * Brand enrichment for the Logistics tab: known airlines, hotel chains and
 * rental-car companies, matched loosely by name so a facilitator typing
 * "southwest" or "Omni Las Colinas" gets the brand's logo and (for airlines)
 * a deep link to its manage-reservation page.
 *
 * Logos come from Google's public favicon service by domain — no API key,
 * cached hard by Google, and gracefully absent when a brand is unknown.
 */

export type TravelBrandKind = 'airline' | 'hotel' | 'rentalCar';

export interface TravelBrand {
  readonly name: string;
  readonly domain: string;
  /** Where "Manage booking" lands, when the brand has a stable page. */
  readonly manageUrl?: string;
  /**
   * A lookup URL template with `{confirmation}` / `{lastName}` slots, for
   * brands whose manage page prefills from query params. Unofficial — at
   * worst the page ignores the params and loads its normal lookup form, so
   * the link can only ever be as good as `manageUrl` or better.
   */
  readonly bookingUrlTemplate?: string;
}

const AIRLINES: readonly TravelBrand[] = [
  {
    name: 'Southwest',
    domain: 'southwest.com',
    manageUrl: 'https://www.southwest.com/air/manage-reservation/',
    bookingUrlTemplate:
      'https://www.southwest.com/air/manage-reservation/index.html?confirmationNumber={confirmation}&passengerLastName={lastName}',
  },
  { name: 'Delta', domain: 'delta.com', manageUrl: 'https://www.delta.com/mytrips/' },
  {
    name: 'American Airlines',
    domain: 'aa.com',
    manageUrl: 'https://www.aa.com/reservation/view/find-your-trip',
    bookingUrlTemplate:
      'https://www.aa.com/reservation/view/find-your-trip?recordLocator={confirmation}&lastName={lastName}',
  },
  {
    name: 'United',
    domain: 'united.com',
    manageUrl: 'https://www.united.com/en/us/manageres/mytrips',
    bookingUrlTemplate:
      'https://www.united.com/en/us/manageres/mytrips?confirmationNumber={confirmation}&lastName={lastName}',
  },
  { name: 'Alaska Airlines', domain: 'alaskaair.com', manageUrl: 'https://www.alaskaair.com/booking/manage-reservation' },
  { name: 'JetBlue', domain: 'jetblue.com', manageUrl: 'https://www.jetblue.com/manage-trips' },
  { name: 'Spirit', domain: 'spirit.com', manageUrl: 'https://www.spirit.com/my-trips/find-trip' },
  { name: 'Frontier', domain: 'flyfrontier.com', manageUrl: 'https://www.flyfrontier.com/travel/my-trips/manage-trip/' },
  { name: 'Allegiant', domain: 'allegiantair.com', manageUrl: 'https://www.allegiantair.com/manage-travel' },
  { name: 'Hawaiian Airlines', domain: 'hawaiianairlines.com', manageUrl: 'https://www.hawaiianairlines.com/manage' },
  { name: 'Sun Country', domain: 'suncountry.com', manageUrl: 'https://suncountry.com/manage-my-trip' },
  { name: 'Breeze', domain: 'flybreeze.com' },
];

const MARRIOTT_LOOKUP = 'https://www.marriott.com/reservation/lookupReservation.mi';
const HILTON_LOOKUP = 'https://www.hilton.com/en/find-reservation/';

const HOTELS: readonly TravelBrand[] = [
  { name: 'Marriott', domain: 'marriott.com', manageUrl: MARRIOTT_LOOKUP },
  { name: 'Courtyard', domain: 'marriott.com', manageUrl: MARRIOTT_LOOKUP },
  { name: 'Residence Inn', domain: 'marriott.com', manageUrl: MARRIOTT_LOOKUP },
  { name: 'Westin', domain: 'marriott.com', manageUrl: MARRIOTT_LOOKUP },
  { name: 'Sheraton', domain: 'marriott.com', manageUrl: MARRIOTT_LOOKUP },
  { name: 'Ritz-Carlton', domain: 'ritzcarlton.com', manageUrl: MARRIOTT_LOOKUP },
  { name: 'Hilton', domain: 'hilton.com', manageUrl: HILTON_LOOKUP },
  { name: 'DoubleTree', domain: 'hilton.com', manageUrl: HILTON_LOOKUP },
  { name: 'Embassy Suites', domain: 'hilton.com', manageUrl: HILTON_LOOKUP },
  { name: 'Hampton Inn', domain: 'hilton.com', manageUrl: HILTON_LOOKUP },
  { name: 'Hampton', domain: 'hilton.com', manageUrl: HILTON_LOOKUP },
  { name: 'Hyatt', domain: 'hyatt.com' },
  { name: 'Omni', domain: 'omnihotels.com' },
  { name: 'InterContinental', domain: 'ihg.com' },
  { name: 'Holiday Inn', domain: 'ihg.com' },
  { name: 'Kimpton', domain: 'ihg.com' },
  { name: 'Fairmont', domain: 'fairmont.com' },
  { name: 'Four Seasons', domain: 'fourseasons.com' },
  { name: 'Loews', domain: 'loewshotels.com' },
  { name: 'Wyndham', domain: 'wyndhamhotels.com' },
  { name: 'Best Western', domain: 'bestwestern.com' },
  { name: 'Radisson', domain: 'radissonhotels.com' },
  { name: 'Gaylord', domain: 'marriott.com' },
  { name: 'W Hotel', domain: 'marriott.com' },
];

const RENTAL_CARS: readonly TravelBrand[] = [
  { name: 'National', domain: 'nationalcar.com', manageUrl: 'https://www.nationalcar.com/en/reservation/lookup.html' },
  { name: 'Enterprise', domain: 'enterprise.com', manageUrl: 'https://www.enterprise.com/en/reserve/lookup.html' },
  { name: 'Hertz', domain: 'hertz.com', manageUrl: 'https://www.hertz.com/rentacar/reservation/reviewmodifycancel/templates/rentalDetails.jsp' },
  { name: 'Avis', domain: 'avis.com', manageUrl: 'https://www.avis.com/en/reservation/view-modify-cancel' },
  { name: 'Budget', domain: 'budget.com', manageUrl: 'https://www.budget.com/en/reservation/view-modify-cancel' },
  { name: 'Alamo', domain: 'alamo.com', manageUrl: 'https://www.alamo.com/en/reserve/lookup.html' },
  { name: 'Sixt', domain: 'sixt.com' },
  { name: 'Thrifty', domain: 'thrifty.com' },
  { name: 'Dollar', domain: 'dollar.com' },
  { name: 'Turo', domain: 'turo.com' },
];

const CATALOG: Record<TravelBrandKind, readonly TravelBrand[]> = {
  airline: AIRLINES,
  hotel: HOTELS,
  rentalCar: RENTAL_CARS,
};

/** Known brand names for a kind — feeds input datalist suggestions. */
export function brandSuggestions(kind: TravelBrandKind): readonly string[] {
  return [...new Set(CATALOG[kind].map((brand) => brand.name))];
}

/** The full brand list for a kind — feeds the logo dropdowns. */
export function brandsFor(kind: TravelBrandKind): readonly TravelBrand[] {
  return CATALOG[kind];
}

/**
 * The best link to a specific booking: the brand's prefill template when it
 * has one (confirmation + traveler last name substituted in), else the
 * manage/lookup page.
 *
 * @param brand - The matched brand.
 * @param confirmation - The booking's confirmation number.
 * @param lastName - The traveler's last name.
 * @returns A URL, or undefined when the brand has no manage page at all.
 */
export function bookingLink(
  brand: TravelBrand | undefined,
  confirmation: string | undefined,
  lastName: string | undefined,
): string | undefined {
  if (!brand) return undefined;
  if (brand.bookingUrlTemplate && confirmation) {
    return brand.bookingUrlTemplate
      .replace('{confirmation}', encodeURIComponent(confirmation))
      .replace('{lastName}', encodeURIComponent(lastName ?? ''));
  }
  return brand.manageUrl;
}

/**
 * Loosely matches free-typed text ("omni las colinas", "SOUTHWEST") to a
 * known brand. Longest brand name wins so "Holiday Inn" beats "Inn".
 *
 * @param text - Whatever the facilitator typed.
 * @param kind - Which catalog to match against.
 * @returns The brand, or undefined for unknown names.
 */
export function matchBrand(
  text: string | undefined,
  kind: TravelBrandKind,
): TravelBrand | undefined {
  if (!text?.trim()) return undefined;
  const haystack = text.toLowerCase();
  return [...CATALOG[kind]]
    .sort((a, b) => b.name.length - a.name.length)
    .find((brand) => haystack.includes(brand.name.toLowerCase()));
}

/** The brand's logo URL (Google favicon service — keyless, cached, no PII). */
export function brandLogoUrl(brand: TravelBrand): string {
  return `https://www.google.com/s2/favicons?domain=${encodeURIComponent(brand.domain)}&sz=128`;
}

/** Nights between two yyyy-mm-dd dates, when both parse. */
export function nightsBetween(
  checkIn: string | undefined,
  checkOut: string | undefined,
): number | undefined {
  if (!checkIn || !checkOut) return undefined;
  const from = Date.parse(checkIn);
  const to = Date.parse(checkOut);
  if (Number.isNaN(from) || Number.isNaN(to) || to <= from) return undefined;
  return Math.round((to - from) / 86_400_000);
}

/** "Mon, Oct 26 → Wed, Oct 28" for a stay/pickup range. */
export function formatDateRange(
  checkIn: string | undefined,
  checkOut: string | undefined,
): string | undefined {
  const format = (value: string | undefined): string | undefined => {
    if (!value) return undefined;
    const parsed = Date.parse(`${value}T12:00:00`);
    if (Number.isNaN(parsed)) return undefined;
    return new Date(parsed).toLocaleDateString(undefined, {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
    });
  };
  const from = format(checkIn);
  const to = format(checkOut);
  if (from && to) return `${from} → ${to}`;
  return from ?? to;
}
